import React, { useEffect, useRef, useState } from 'react';
import { StylishNewButton } from './StylishNewButton';
import classNames from 'classnames';

type PratusTextAreaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'defaultValue' | 'onChange'
> & {
  label?: string;
  onSave?: (value: string) => void;
  onCancel?: () => void;
  defaultValue?: string;
  value?: string;
};

// Updates the height of a <textarea> when the value changes.
export const useAutosizeTextArea = (
  textAreaRef: HTMLTextAreaElement | null,
  value: string,
  rows: number
) => {
  useEffect(() => {
    if (textAreaRef) {
      // Get computed line height for accurate calculations
      const computedStyle = window.getComputedStyle(textAreaRef);
      const lineHeight = parseInt(computedStyle.lineHeight) || 24; // Fallback to 24 if can't parse
      const minHeight =
        rows * lineHeight +
        parseInt(computedStyle.paddingTop) +
        parseInt(computedStyle.paddingBottom);

      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      textAreaRef.style.height = '0px';

      const scrollHeight = Math.max(textAreaRef.scrollHeight, minHeight) + 2;

      // We then set the height directly, outside of the render loop
      // Trying to set this with state or a ref will product an incorrect value.
      textAreaRef.style.height = scrollHeight + 'px';
    }
  }, [textAreaRef, value, rows]);
};

export default function PratusTextArea({
  minLength = 0,
  maxLength,
  value,
  defaultValue,
  onCancel,
  onSave,
  rows = 3,
  ...props
}: PratusTextAreaProps) {
  const isControlled = value !== undefined;
  const initialValue = isControlled ? (value ?? '') : (defaultValue ?? '');

  const [inputValue, setInputValue] = useState(initialValue);
  const [lastSavedValue, setLastSavedValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);

  // Sync internal state when external value/defaultValue changes.
  // If there are no unsaved edits (inputValue === lastSavedValue), update both.
  // If the user has unsaved edits, preserve the draft but refresh lastSavedValue.
  useEffect(() => {
    const next = value !== undefined ? (value ?? '') : (defaultValue ?? '');
    setLastSavedValue(next);
    if (inputValue === lastSavedValue) {
      setInputValue(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultValue]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textareaRef.current, inputValue, rows);

  const handleSave: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.currentTarget.blur();
    textareaRef.current?.blur();
    onSave?.(inputValue);
    setLastSavedValue(inputValue);
  };

  const onInputCancel: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.currentTarget.blur();
    textareaRef.current?.blur();
    onCancel?.();
    setInputValue(lastSavedValue);
  };

  const isError = inputValue.length < minLength;

  return (
    <div className={classNames('pratus-input-field mb-1', props.className)}>
      {props.label && (
        <label className="form-label" htmlFor={props.id}>
          {props.label}
        </label>
      )}
      <TextArea
        {...props}
        ref={textareaRef}
        className={'mb-2 form-control'}
        value={inputValue}
        maxLength={maxLength}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        spellCheck={isFocused}
        onChange={(e) => setInputValue(e.target.value)}
      />
      {maxLength !== undefined && (
        <div className="form-text text-muted mb-1 text-start pratus-textarea-char-count">
          {inputValue.length} / {maxLength}
        </div>
      )}
      <div className="d-flex justify-content-between">
        <div className="btn-container">
          <StylishNewButton
            className="me-2 button--sml"
            onClick={handleSave}
            disabled={isError}
          >
            Save
          </StylishNewButton>
          <StylishNewButton
            className="button--secondary button--sml"
            onClick={onInputCancel}
          >
            Cancel
          </StylishNewButton>
        </div>
        <div className="">
          <span
            className="form-text no-icon text-danger fs-6"
            style={{
              visibility:
                inputValue.length > 0 && isError ? 'visible' : 'hidden',
            }}
          >
            Enter at least {minLength} characters
          </span>
        </div>
      </div>
    </div>
  );
}

type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ style, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        {...props}
        style={{ borderRadius: '6px', paddingLeft: '9px', ...style }}
      />
    );
  }
);
TextArea.displayName = 'BaseTextArea';
